/**
 * Admin User Detail Page
 *
 * Everything that used to live in the 450px drawer on the users list, as a page of its own.
 *
 * The drawer held five sections plus four toggles and a delete button, stacked in one scrolling
 * column. On a desktop it was cramped; on a phone it covered the screen and the actions at the
 * bottom were unreachable without scrolling past every unrelated control above them. Splitting it
 * into tabs means one job is on screen at a time, and `?tab=` makes each of them linkable — you
 * can send someone straight to a user's credits.
 *
 * Each tab's panel owns its own loading and its own mutations, so opening a user costs one request
 * (the user) and the subscription/credits/connections calls only happen when their tab is opened.
 * The drawer fired all four every time a row was clicked.
 */

import { useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { Alert, Box, Breadcrumbs, Button, Link, Skeleton } from '@mui/material'
import {
  CardGiftcard,
  DirectionsWalk,
  EmojiEvents,
  Hiking,
  Info,
  LockReset,
  Router,
  Share,
  Shield,
  WorkspacePremium,
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { PageShell } from '@components/common/PageShell'
import { ResponsiveTabs, type ResponsiveTabItem } from '@components/common/ResponsiveTabs'
import { adminApi } from '@api/endpoints/admin'
import { UserHeader } from '../components/users/UserHeader'
import { UserOverviewPanel } from '../components/users/UserOverviewPanel'
import { UserAccessPanel } from '../components/users/UserAccessPanel'
import { UserSubscriptionPanel } from '../components/users/UserSubscriptionPanel'
import { UserCreditsPanel } from '../components/users/UserCreditsPanel'
import { UserSecurityPanel } from '../components/users/UserSecurityPanel'
import { UserNetworkPanel } from '../components/users/UserNetworkPanel'
import { UserAchievementsPanel } from '../components/users/UserAchievementsPanel'
import { UserAdventuresPanel } from '../components/users/UserAdventuresPanel'
import { UserActivityPanel } from '../components/users/UserActivityPanel'
import { UserReferralsPanel } from '../components/users/UserReferralsPanel'
import { adminUserKey } from '../components/users/userQueryKeys'
import { errorMessage } from '../components/users/userFormatting'
import { useIsAdmin } from '../components/users/useIsAdmin'

/**
 * Tabs a Moderator does not get. Moderators open this page READ-ONLY (every write control is hidden for them and
 * Admin-only on the API): Security holds only actions (password reset, share-code rotation), and Network is IP
 * history that leads into the Admin-only IP tools.
 */
const ADMIN_ONLY_TABS = new Set(['security', 'network'])

const TABS: ResponsiveTabItem[] = [
  { key: 'overview', label: 'Overview', icon: <Info fontSize="small" /> },
  // What the user earned and what was tracked about them, for correction (Backlog 050).
  { key: 'achievements', label: 'Achievements', icon: <EmojiEvents fontSize="small" /> },
  { key: 'adventures', label: 'Adventures', icon: <Hiking fontSize="small" /> },
  { key: 'activity', label: 'Activity', icon: <DirectionsWalk fontSize="small" /> },
  { key: 'access', label: 'Access', icon: <Shield fontSize="small" /> },
  { key: 'subscription', label: 'Subscription', icon: <WorkspacePremium fontSize="small" /> },
  { key: 'credits', label: 'Credits', icon: <CardGiftcard fontSize="small" /> },
  { key: 'security', label: 'Security', icon: <LockReset fontSize="small" /> },
  { key: 'network', label: 'Network', icon: <Router fontSize="small" /> },
  // Who referred them and whom they referred, against the 50 bar (Backlog 061 step 1). No money.
  { key: 'referrals', label: 'Referrals', icon: <Share fontSize="small" /> },
]

export default function AdminUserDetailPage() {
  const { userId = '' } = useParams<{ userId: string }>()
  const [tab, setTab] = useState('overview')
  const isAdmin = useIsAdmin()
  const tabs = isAdmin ? TABS : TABS.filter((t) => !ADMIN_ONLY_TABS.has(t.key))
  const shownTab = tabs.some((t) => t.key === tab) ? tab : 'overview'

  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: adminUserKey(userId),
    queryFn: () => adminApi.getUser(userId),
    enabled: Boolean(userId),
  })

  const breadcrumbs = (
    <Breadcrumbs>
      <Link component={RouterLink} to="/users" underline="hover" color="inherit">
        Users
      </Link>
      <span>{user?.displayName ?? 'User'}</span>
    </Breadcrumbs>
  )

  if (error) {
    return (
      <PageShell title="User" breadcrumbs={breadcrumbs}>
        <Alert
          severity="error"
          action={
            <Button component={RouterLink} to="/users" size="small" color="inherit">
              Back to users
            </Button>
          }
        >
          {errorMessage(error, 'Failed to load this user')}
        </Alert>
      </PageShell>
    )
  }

  if (isLoading || !user) {
    return (
      <PageShell title="User" breadcrumbs={breadcrumbs}>
        <Skeleton variant="rounded" height={140} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={44} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={280} />
      </PageShell>
    )
  }

  return (
    <PageShell title={user.displayName || user.email || 'User'} breadcrumbs={breadcrumbs}>
      <UserHeader user={user} />

      <ResponsiveTabs tabs={tabs} value={shownTab} onChange={setTab} sx={{ mb: 2 }} />

      {/* Only the active tab is mounted, which is also what keeps the other tabs' queries from
          firing until someone asks for them. */}
      <Box>
        {shownTab === 'overview' && <UserOverviewPanel user={user} />}
        {shownTab === 'achievements' && <UserAchievementsPanel userId={user.id} />}
        {shownTab === 'adventures' && <UserAdventuresPanel userId={user.id} />}
        {shownTab === 'activity' && <UserActivityPanel userId={user.id} />}
        {shownTab === 'access' && <UserAccessPanel user={user} />}
        {shownTab === 'subscription' && <UserSubscriptionPanel user={user} />}
        {shownTab === 'credits' && <UserCreditsPanel userId={user.id} />}
        {shownTab === 'security' && <UserSecurityPanel user={user} />}
        {shownTab === 'network' && <UserNetworkPanel user={user} />}
        {shownTab === 'referrals' && <UserReferralsPanel userId={user.id} />}
      </Box>
    </PageShell>
  )
}
